import type {
  IRPattern,
  IRExpression,
  IRInstruction,
  IRStep,
  IRDelta,
  IRSegment,
  IRScope,
  IRValue,
  IRNode,
  IRType,
  IRCounter,
} from '../types/ir';
import type { KnittingEvent } from '../types/session';

export interface IRState {
  counters: IRScope;
  params: IRScope;
  currentRow: number; // Global row count (total rows knitted, excluding notes)
  currentInstruction: string;
  activeNodeId: string | null;
  activeStepKind: 'row' | 'note' | null;
  isComplete: boolean;
  totalRows: number; // Total knittable rows in the pattern, excluding notes
  path: { id: string; name: string }[];
  // For each container (pattern / segment / group) reachable in the current
  // path, "row" is the 1-indexed position of the active row within it
  // (counted across all visits if the container is iterated), and "total" is
  // the total rows the container will yield. Notes are excluded.
  containerProgress: Record<string, { row: number; total: number }>;
}

/**
 * Evaluates an IRExpression in a given scope.
 */
export function evaluateExpression(expr: IRExpression, scope: IRScope): IRValue {
  switch (expr.kind) {
    case 'literal':
      return expr.value;
    case 'param_ref':
    case 'counter_ref':
      if (!(expr.name in scope)) {
        // Fallback or error?
        return 0;
      }
      return scope[expr.name];
    case 'binop': {
      const left = evaluateExpression(expr.left, scope);
      const right = evaluateExpression(expr.right, scope);
      switch (expr.op) {
        case '+':
          return (left as number) + (right as number);
        case '-':
          return (left as number) - (right as number);
        case '*':
          return (left as number) * (right as number);
        case '/':
          return Math.floor((left as number) / (right as number));
        case '%':
          return (left as number) % (right as number);
        case '==':
          return left === right;
        case '!=':
          return left !== right;
        case '<':
          return left < right;
        case '<=':
          return left <= right;
        case '>':
          return left > right;
        case '>=':
          return left >= right;
        case 'and':
          return Boolean(left) && Boolean(right);
        case 'or':
          return Boolean(left) || Boolean(right);
        default:
          return 0;
      }
    }
    case 'unop': {
      const operand = evaluateExpression(expr.operand, scope);
      switch (expr.op) {
        case 'not':
          return !operand;
        case 'neg':
          return -(operand as number);
        default:
          return 0;
      }
    }
    default:
      return 0;
  }
}

const TYPE_MATCHES: Record<IRType, (v: unknown) => boolean> = {
  int: (v) => typeof v === 'number',
  text: (v) => typeof v === 'string',
  bool: (v) => typeof v === 'boolean',
};

/**
 * Calculates the current state by replaying history.
 * This is an iterative process: we start at the beginning of the pattern
 * and apply each 'NEXT_ROW' event by advancing the pattern logic.
 */
export function calculateIRState(
  pattern: IRPattern,
  history: KnittingEvent[],
  paramOverrides?: Record<string, IRValue>,
): IRState {
  const params: IRScope = {};
  for (const p of pattern.params ?? []) {
    const override = paramOverrides?.[p.name];
    if (override !== undefined && TYPE_MATCHES[p.type](override)) {
      params[p.name] = override;
    } else {
      params[p.name] = evaluateExpression(p.default, params);
    }
  }

  const counters: IRScope = { ...params };
  // Initialize pattern-level counters
  for (const c of pattern.counters ?? []) {
    if (c.kind === 'counter_mutable') {
      counters[c.name] = evaluateExpression(c.init, counters);
    } else {
      counters[c.name] = evaluateExpression(c.expr, counters);
    }
  }

  const nodeExpansions: Record<string, number> = {};

  const state: IRState = {
    counters,
    params,
    currentRow: 0,
    currentInstruction: 'Start knitting',
    activeNodeId: null,
    activeStepKind: null,
    isComplete: false,
    totalRows: 0,
    path: [],
    containerProgress: {},
  };

  // Pre-process history: drop timer-only events (START/PAUSE/RESUME) since
  // they don't affect row state; collapse PREV_ROW into NEXT_ROW removal;
  // collect nodeExpansions.
  //
  // PREV_ROW pairing: a single Next-tap on the last row of a flexible
  // iteration emits an EXPAND_NODE followed immediately by a NEXT_ROW
  // (ActiveTracker.handleNextRow). Treating those as one user action means
  // the matching PREV must drop both — otherwise the cursor steps back but
  // the expansion is left orphaned, the loop stays grown, and the screen
  // says "Round 2" while the engine still considers it mid-iteration.
  const effectiveHistory: KnittingEvent[] = [];
  for (const event of history) {
    if (event.action === 'START' || event.action === 'PAUSE' || event.action === 'RESUME') {
      continue;
    }
    if (event.action === 'PREV_ROW') {
      for (let i = effectiveHistory.length - 1; i >= 0; i--) {
        if (effectiveHistory[i].action === 'NEXT_ROW') {
          effectiveHistory.splice(i, 1);
          if (i > 0 && effectiveHistory[i - 1].action === 'EXPAND_NODE') {
            const expanded = effectiveHistory[i - 1];
            if (expanded.action === 'EXPAND_NODE' && expanded.nodeId) {
              nodeExpansions[expanded.nodeId] = Math.max(
                0,
                (nodeExpansions[expanded.nodeId] || 0) - 1,
              );
            }
            effectiveHistory.splice(i - 1, 1);
          }
          break;
        }
      }
    } else if (event.action === 'EXPAND_NODE' && event.nodeId) {
      nodeExpansions[event.nodeId] = (nodeExpansions[event.nodeId] || 0) + 1;
      effectiveHistory.push(event);
    } else {
      effectiveHistory.push(event);
    }
  }

  // Calculate totalRows + per-container totals by running a shadow iterator
  // that simulates the engine: applying deltas + recomputing derived
  // counters between yields so until-predicates depending on counters can
  // terminate naturally. Notes don't count as rows.
  const shadowCounters = { ...state.counters };
  const shadowStructural: Record<string, number> = {};
  const shadowIterator = patternIterator(pattern, shadowCounters, nodeExpansions);
  let totalRows = 0;
  let stepCount = 0;
  const containerTotals: Record<string, number> = {};
  let shadow = shadowIterator.next();
  while (!shadow.done) {
    if (shadow.value.kind === 'row') {
      totalRows++;
      for (const item of shadow.value.path) {
        containerTotals[item.id] = (containerTotals[item.id] || 0) + 1;
      }
    }
    stepCount++;
    if (stepCount > 10000) break; // Safety break
    applyDeltas(shadow.value.deltas, shadowCounters);
    updateLinks(pattern, shadowCounters, shadowStructural);
    updateDerivedCounters(pattern, shadowCounters, shadow.value.path);
    shadow = shadowIterator.next();
  }
  state.totalRows = totalRows;

  // The "knitting engine" needs to keep track of its position in the tree.
  const iterator = patternIterator(pattern, state.counters, nodeExpansions);

  // Per-container current-row counter; mirrors containerTotals' shape.
  // Incremented for every yielded row (including the active one) so it
  // represents "row N of total" within each ancestor container.
  const containerCurrent: Record<string, number> = {};
  const tickContainers = (path: { id: string; name: string }[]) => {
    for (const item of path) {
      containerCurrent[item.id] = (containerCurrent[item.id] || 0) + 1;
    }
  };

  // Get initial step
  let current = iterator.next();
  if (!current.done) {
    state.currentInstruction = current.value.instruction;
    state.activeNodeId = current.value.nodeId;
    state.activeStepKind = current.value.kind;
    state.path = current.value.path;
    if (current.value.kind === 'row') tickContainers(current.value.path);
  } else {
    state.isComplete = true;
    state.currentInstruction = 'No instruction';
  }

  // Previously-seen value of each link source, carried across advances so a
  // link fires once per structural-counter change. Local to this replay pass.
  const lastStructural: Record<string, number> = {};

  for (const event of effectiveHistory) {
    if (event.action === 'NEXT_ROW') {
      // Apply deltas/links for the CURRENT step before moving to next.
      // Notes have no deltas, so this is a no-op for them, but we still
      // refresh derived counters so the next step sees consistent values.
      if (!current.done) {
        applyDeltas(current.value.deltas, state.counters);
        updateLinks(pattern, state.counters, lastStructural);
        updateDerivedCounters(pattern, state.counters, current.value.path);
      }

      current = iterator.next();
      if (!current.done) {
        if (current.value.kind === 'row') {
          state.currentRow++;
          tickContainers(current.value.path);
        }
        state.currentInstruction = current.value.instruction;
        state.activeNodeId = current.value.nodeId;
        state.activeStepKind = current.value.kind;
        state.path = current.value.path;
      } else {
        state.isComplete = true;
        state.currentInstruction = 'No instruction';
        state.activeStepKind = null;
      }
    }
  }

  // Materialize containerProgress for every container we've encountered.
  for (const id of Object.keys(containerTotals)) {
    state.containerProgress[id] = {
      row: containerCurrent[id] || 0,
      total: containerTotals[id],
    };
  }

  return state;
}

interface StepInfo {
  instruction: string;
  nodeId: string;
  deltas: IRDelta[];
  path: { id: string; name: string }[];
  kind: 'row' | 'note';
}

/**
 * Generator that yields each knittable row in the pattern.
 */
function* patternIterator(
  pattern: IRPattern,
  counters: IRScope,
  nodeExpansions: Record<string, number>,
): Generator<StepInfo> {
  if (!pattern.segments || !Array.isArray(pattern.segments)) {
    console.error('Invalid pattern structure: segments missing or not an array', pattern);
    return;
  }
  const path = [{ id: pattern.id, name: pattern.name }];
  for (const segment of pattern.segments) {
    yield* segmentIterator(segment, counters, nodeExpansions, path);
  }
}

function* segmentIterator(
  segment: IRSegment,
  counters: IRScope,
  nodeExpansions: Record<string, number>,
  parentPath: { id: string; name: string }[],
): Generator<StepInfo> {
  const path = [...parentPath, { id: segment.id, name: segment.name }];
  // Initialize segment-scoped counters on entry. Mutable counters need their
  // initial value seeded so deltas and until-predicates work; derived
  // counters are also seeded here and re-evaluated by updateDerivedCounters
  // between yields.
  for (const c of segment.counters ?? []) {
    if (c.kind === 'counter_mutable') {
      counters[c.name] = evaluateExpression(c.init, counters);
    } else {
      counters[c.name] = evaluateExpression(c.expr, counters);
    }
  }
  // Setup steps
  for (const step of segment.setup) {
    yield* stepIterator(step, counters, nodeExpansions, path, 0);
  }
  // Body steps
  for (const step of segment.body) {
    yield* stepIterator(step, counters, nodeExpansions, path, 0);
  }
}

function* stepIterator(
  step: IRStep,
  counters: IRScope,
  nodeExpansions: Record<string, number>,
  parentPath: { id: string; name: string }[],
  depth = 0,
): Generator<StepInfo> {
  if (depth > 20) return; // Recursion safety break

  if (step.kind === 'row') {
    const instruction = resolveInstruction(step.instruction, counters);
    yield {
      instruction: instruction.text,
      nodeId: step.id,
      deltas: [...step.deltas, ...instruction.deltas],
      path: [...parentPath],
      kind: 'row',
    };
  } else if (step.kind === 'group') {
    const path = [...parentPath, { id: step.id, name: step.iter_name || 'Group' }];

    // Initialize group-scoped counters once on entry, before the loop.
    for (const c of step.counters ?? []) {
      if (c.kind === 'counter_mutable') {
        counters[c.name] = evaluateExpression(c.init, counters);
      } else {
        counters[c.name] = evaluateExpression(c.expr, counters);
      }
    }

    if (step.count.kind === 'count_fixed') {
      const baseCount = evaluateExpression(step.count.expr, counters) as number;
      const expandedCount = baseCount + (nodeExpansions[step.id] || 0);
      for (let i = 0; i < expandedCount; i++) {
        if (step.iter_name) {
          counters[step.iter_name] = i + 1;
        }
        for (const child of step.body) {
          yield* stepIterator(child, counters, nodeExpansions, path, depth + 1);
        }
      }
    } else if (step.count.kind === 'count_until_pred') {
      let i = 0;
      while (!evaluateExpression(step.count.predicate, counters)) {
        if (step.iter_name) {
          counters[step.iter_name] = i + 1;
        }
        for (const child of step.body) {
          yield* stepIterator(child, counters, nodeExpansions, path, depth + 1);
        }
        i++;
        if (i > 1000) break; // Safety break
      }
    } else if (step.count.kind === 'count_until_text') {
      // Variable-length: at least one iteration, plus one per EXPAND_NODE event.
      // The user signals "done with this section" by advancing without expanding.
      const iterations = Math.min(1 + (nodeExpansions[step.id] || 0), 10000);
      for (let i = 0; i < iterations; i++) {
        if (step.iter_name) {
          counters[step.iter_name] = i + 1;
        }
        for (const child of step.body) {
          yield* stepIterator(child, counters, nodeExpansions, path, depth + 1);
        }
      }
    }
  } else if (step.kind === 'note') {
    yield {
      instruction: String(evaluateExpression(step.text_expr, counters)),
      nodeId: step.id,
      deltas: [],
      path: [...parentPath],
      kind: 'note',
    };
  }
}

/**
 * Finds a node by ID in the IR pattern.
 */
export function getNodeById(pattern: IRPattern, id: string): IRNode | null {
  if (pattern.id === id) return pattern;
  for (const segment of pattern.segments) {
    if (segment.id === id) return segment;
    const found = findInSteps(segment.setup, id) || findInSteps(segment.body, id);
    if (found) return found;
  }
  return null;
}

/**
 * Deep clones an IR structure (Pattern, Segment, or Step), assigning new unique IDs.
 */
export function cloneIRNode<T>(node: T): T {
  if (!node || typeof node !== 'object') return node;

  const clone: Record<string, unknown> = Array.isArray(node)
    ? (node.map((item) => cloneIRNode(item)) as unknown as Record<string, unknown>)
    : { ...(node as Record<string, unknown>) };

  if (typeof clone.id === 'string') {
    clone.id = `n_${crypto.randomUUID().slice(0, 8)}`;
  }

  for (const key in clone) {
    if (typeof clone[key] === 'object' && clone[key] !== null) {
      clone[key] = cloneIRNode(clone[key]);
    }
  }

  return clone as T;
}

function findInSteps(steps: IRStep[], id: string): IRStep | null {
  for (const step of steps) {
    if (step.id === id) return step;
    if (step.kind === 'group') {
      const found = findInSteps(step.body, id);
      if (found) return found;
    }
  }
  return null;
}

function resolveInstruction(
  inst: IRInstruction,
  counters: IRScope,
): { text: string; deltas: IRDelta[] } {
  if (inst.kind === 'instruction_text') {
    return { text: String(evaluateExpression(inst.expr, counters)), deltas: [] };
  }
  for (const c of inst.cases) {
    if (evaluateExpression(c.when, counters)) {
      const inner = resolveInstruction(c.row.instruction, counters);
      return { text: inner.text, deltas: [...c.row.deltas, ...inner.deltas] };
    }
  }
  if (inst.else) {
    const inner = resolveInstruction(inst.else.instruction, counters);
    return { text: inner.text, deltas: [...inst.else.deltas, ...inner.deltas] };
  }
  return { text: 'Knit', deltas: [] };
}

function applyDeltas(deltas: IRDelta[], counters: IRScope) {
  for (const d of deltas) {
    if (d.target.kind === 'counter_ref') {
      const deltaValue = evaluateExpression(d.expr, counters) as number;
      counters[d.target.name] = ((counters[d.target.name] as number) || 0) + deltaValue;
    }
  }
}

/** Every counter declared anywhere in the pattern (pattern/segment/group scope). */
function collectAllCounters(pattern: IRPattern): IRCounter[] {
  const out: IRCounter[] = [...(pattern.counters ?? [])];
  const walkSteps = (steps: IRStep[]) => {
    for (const s of steps) {
      if (s.kind === 'group') {
        out.push(...(s.counters ?? []));
        walkSteps(s.body);
      }
    }
  };
  for (const seg of pattern.segments) {
    out.push(...(seg.counters ?? []));
    walkSteps(seg.setup);
    walkSteps(seg.body);
  }
  return out;
}

// A link fires when its source structural counter (a group's iter_name, written
// into the flat `counters` scope by the iterator) changes value since the last
// advance — i.e. once per iteration — gated by an optional `when` predicate.
// `lastStructural` carries the previously-seen value of each link source across
// advances; the caller owns it (one per replay/shadow pass) so the function
// stays free of module state.
function updateLinks(
  pattern: IRPattern,
  counters: IRScope,
  lastStructural: Record<string, number>,
) {
  const allCounters = collectAllCounters(pattern);

  // Phase 1: apply deltas for links whose source changed since the last advance.
  for (const counter of allCounters) {
    if (counter.kind !== 'counter_mutable') continue;
    for (const link of counter.links) {
      if (link.source.kind !== 'counter_ref') continue;
      const srcName = link.source.name;
      const cur = counters[srcName];
      if (typeof cur !== 'number') continue;
      if (lastStructural[srcName] === cur) continue; // unchanged → don't fire
      if (link.when == null || evaluateExpression(link.when, counters)) {
        const delta = evaluateExpression(link.delta, counters) as number;
        counters[counter.name] = ((counters[counter.name] as number) || 0) + delta;
      }
    }
  }

  // Phase 2: record the current value of every link source AFTER firing, so two
  // counters keyed to the same source both observe one change before it's
  // marked as seen.
  for (const counter of allCounters) {
    if (counter.kind !== 'counter_mutable') continue;
    for (const link of counter.links) {
      if (link.source.kind !== 'counter_ref') continue;
      const cur = counters[link.source.name];
      if (typeof cur === 'number') lastStructural[link.source.name] = cur;
    }
  }
}

function updateDerivedCounters(
  pattern: IRPattern,
  counters: IRScope,
  currentPath?: { id: string; name: string }[],
) {
  // Re-evaluate all pattern-level derived counters
  for (const c of pattern.counters ?? []) {
    if (c.kind === 'counter_derived') {
      counters[c.name] = evaluateExpression(c.expr, counters);
    }
  }

  // Only re-evaluate counters for segments in the current path
  if (currentPath) {
    const segmentIds = new Set(pattern.segments.map((s) => s.id));
    for (const item of currentPath) {
      if (segmentIds.has(item.id)) {
        const seg = pattern.segments.find((s) => s.id === item.id);
        if (seg) {
          for (const c of seg.counters ?? []) {
            if (c.kind === 'counter_derived') {
              counters[c.name] = evaluateExpression(c.expr, counters);
            }
          }
        }
      }
    }
  } else {
    // Fallback: update all segments if no path provided (rare)
    for (const seg of pattern.segments ?? []) {
      for (const c of seg.counters ?? []) {
        if (c.kind === 'counter_derived') {
          counters[c.name] = evaluateExpression(c.expr, counters);
        }
      }
    }
  }
}
